import { Button, Table, theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CustomerDetailResponse } from '@/types/api/customer';
import type { CustomerPaymentResponse } from '@/types/api/customer';
import { hasHoldStatus, getHoldStatusI18nKey } from '@/types/api/customer';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import TableContainer from '@/components/common/TableContainer';

interface PaymentSectionProps {
  customer: CustomerDetailResponse;
  paymentData: CustomerPaymentResponse | null;
  canEdit: boolean;
  onEditFinancialInfo: () => void;
}

const PaymentSection = ({ customer, paymentData, canEdit, onEditFinancialInfo }: PaymentSectionProps) => {
  const { t } = useTranslation('customers');
  const { token } = theme.useToken();
  const elevation = useElevation();

  return (
    <div>
      {/* Financial Information Header with Edit Button */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('customer.sections.financialInformation')}
        </h3>
        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditFinancialInfo}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        >
          {t('customer.actions.edit')}
        </Button>
      </div>

      <div className="tw-grid tw-grid-cols-3 tw-gap-6 tw-mb-6">
        {/* Payment Details Card */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('customer.sections.paymentDetails')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.labels.deposit')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.depositPercentage != null ? `${paymentData.depositPercentage}%` : '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.labels.cashDiscount')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.cashDiscountPercentage != null ? `${paymentData.cashDiscountPercentage}%` : '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.labels.terms')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.paymentTerm?.customTermName || '-'} {paymentData?.dueInDays ? `(${paymentData.dueInDays} ${t('customer.labels.days')})` : ''}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.primaryCurrency')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.primaryCurrency || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Information Card - includes ABN and tax type/rate */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('customer.sections.taxInfo')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.abn')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {customer.localTaxId || '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.taxType')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.taxType || '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.taxRate')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.taxRate != null ? `${paymentData.taxRate}%` : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Credit & Hold Card */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('customer.sections.creditAndHold')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.creditLimit')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.creditLimit != null ? `${paymentData.primaryCurrency || '$'} ${paymentData.creditLimit.toLocaleString()}` : '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.holdStatus')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: hasHoldStatus(paymentData?.holdStatus) ? token.colorError : token.colorText,
                  }}
                >
                  {t(getHoldStatusI18nKey(paymentData?.holdStatus))}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.outstandingBalance')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.primaryCurrency || '-'} -
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('customer.fields.unbilledAmount')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {paymentData?.primaryCurrency || '-'} -
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Currencies Table - matches vendor Financial Information style */}
      <h3
        style={{
          fontSize: token.fontSizeLG,
          fontWeight: token.fontWeightStrong,
          margin: 0,
          marginBottom: '16px',
        }}
      >
        {t('customer.sections.currencies')}
      </h3>
      <TableContainer>
        <Table
          dataSource={[]} // Currencies not available in current API spec
          rowKey={(record: { currency: string }) => record.currency}
          size="small"
          pagination={{
            pageSize: 10,
          }}
          columns={[
            {
              title: t('customer.table.currency'),
              dataIndex: 'currency',
              key: 'currency',
            },
            {
              title: t('customer.table.balance'),
              dataIndex: 'balance',
              key: 'balance',
              render: (balance: number) => (balance ?? 0).toFixed(2),
            },
            {
              title: t('customer.table.unbilled'),
              dataIndex: 'unbilledAmount',
              key: 'unbilledAmount',
              render: (amount: number) => (amount ?? 0).toFixed(2),
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default PaymentSection;
